/**
 * Application shell and persistence wiring (implementation-plan.md §4).
 *
 * The app boots into an immediately usable empty state — never a blocking
 * modal — because reopening a file is a daily, unavoidable chore for the target
 * user rather than an exception.
 */

import { createEmptyDocument, withSavedAt } from '../model/document';
import { DownloadStore } from '../persistence/download-store';
import {
  WORKING_FILENAME,
  copyFilename,
  readDocumentFile,
  type OpenResult,
} from '../persistence/store';
import type { Item } from '../model/types';
import { validateBands } from '../model/validate';
import { buildStandaloneHtml } from '../export/standalone';
import { triggerDownload } from '../persistence/download-store';
import { layoutDocument } from '../render/layout';
import { PRINT_METRICS, SCREEN_METRICS, applyMetrics } from '../render/metrics';
import {
  DEFAULT_PRINT_SETTINGS,
  printLayoutOptions,
  printWarnings,
  type PrintSettings,
} from '../render/print';
import { applyPageSetup } from '../render/page-setup';
import { renderTimeline } from '../render/timeline';
import { renderFilterBar } from './filter-bar';
import { enableFileDrop } from './dropzone';
import { createConfirmDialog } from './confirm-dialog';
import { createEditor } from './editor';
import { createDocumentState } from './state';
import { createStatusPill } from './status-pill';

const TEMPLATE = `
  <header class="app-chrome">
    <div class="title-row">
      <h1>Chrono-Arrow</h1>
      <span data-slot="pill"></span>
    </div>

    <div class="doc-actions">
      <!-- The file itself: what to work on, and getting it back out. -->
      <div class="button-group">
        <button type="button" data-action="open">Ouvrir…</button>
        <button type="button" data-action="new">Nouveau</button>
        <button type="button" data-action="save" data-primary>Enregistrer</button>
        <button type="button" data-action="export-html">Exporter HTML</button>
      </div>

      <!-- Putting it on paper. The orientation is read in the same breath as
           the button it changes, so it travels with it. -->
      <div class="button-group">
        <button type="button" data-action="print">Imprimer</button>
        <span class="print-options">
          <select data-action="print-orientation" title="Orientation de la page">
            <option value="landscape">Paysage</option>
            <option value="portrait">Portrait</option>
          </select>
        </span>
      </div>

      <!-- Filling the chart: the one thing done over and over, so it stands
           alone and in the accent colour. -->
      <div class="button-group">
        <button type="button" data-action="add-entry" data-accent>
          <span class="plus-mark"></span> Ajouter une entrée
        </button>
      </div>

      <span class="spacer"></span>
      <div class="button-group quiet">
        <button type="button" data-action="undo" title="Annuler (Ctrl+Z)">↶</button>
        <button type="button" data-action="redo" title="Rétablir (Ctrl+Y)">↷</button>
      </div>
    </div>

    <div class="messages" data-slot="messages" hidden></div>
  </header>

  <div data-slot="editor" hidden></div>

  <div id="legend" data-slot="legend"></div>

  <main id="document" data-slot="document"></main>

  <div id="bands" data-slot="bands"></div>

  <!-- Adding an entry is the action wanted everywhere, at any scroll position,
       so it also exists as a pill pinned over the page. Labelled rather than a
       bare disc: it is the one control that appears without a row of siblings
       to explain it. -->
  <button type="button" class="fab" data-action="add-entry-fab" data-accent>
    <span class="plus-mark"></span> Ajouter une entrée
  </button>
`;

/** Returns a teardown for the listeners and timers it owns. */
export function mountShell(root: HTMLElement): () => void {
  root.innerHTML = TEMPLATE;

  const controller = new AbortController();
  const { signal } = controller;

  // TypeScript owns the metrics; the ported stylesheet reads them as CSS vars.
  applyMetrics(root, SCREEN_METRICS);

  const slot = <T extends HTMLElement>(name: string): T => {
    const el = root.querySelector<T>(`[data-slot="${name}"]`);
    if (!el) throw new Error(`missing slot: ${name}`);
    return el;
  };
  const action = (name: string): HTMLButtonElement => {
    const el = root.querySelector<HTMLButtonElement>(`[data-action="${name}"]`);
    if (!el) throw new Error(`missing action: ${name}`);
    return el;
  };

  const store = createDocumentState();
  const files = new DownloadStore();

  const pillSlot = slot('pill');
  pillSlot.replaceWith(createStatusPill(store, signal));

  const documentSlot = slot('document');
  const legendSlot = slot('legend');
  const bandsSlot = slot('bands');
  const messagesSlot = slot('messages');
  const editorSlot = slot('editor');
  const saveButton = action('save');
  
  const undoButton = action('undo');
  const redoButton = action('redo');
  const printButton = action('print');
  const exportButton = action('export-html');
  const addEntryButton = action('add-entry');
  const addEntryFab = action('add-entry-fab');

  // Swapped in around printing, so the chart is laid out to the page rather
  // than to the screen (implementation-plan.md §5.1).
  let printGeometry = false;

  // Session state, like the filters: how this chart goes on paper, not what it
  // contains. Changing it must not mark the document as modified.
  const printSettings: PrintSettings = { ...DEFAULT_PRINT_SETTINGS };
  applyPageSetup(printSettings);

  // Session state: filters never reach the document, so toggling one does not
  // mark the file as modified (implementation-plan.md §4.4).
  const hiddenCategories = new Set<string>();
  const hiddenBands = new Set<number>();

  const editor = createEditor({
    onChange: (next) => store.update(next),
    onSelectionChange: () => renderDocument(),
  });
  editorSlot.append(editor.el);

  const confirmDialog = createConfirmDialog();
  root.append(confirmDialog.el);

  /* ---------------------------------------------------------------- rendering */

  /**
   * Errors and warnings read differently because they mean different things: an
   * error is something wrong with the document, a warning is the page telling
   * you it would lay out better another way. Only the first kind is a problem.
   */
  const showMessages = (errors: readonly string[], warnings: readonly string[] = []) => {
    messagesSlot.hidden = errors.length === 0 && warnings.length === 0;
    messagesSlot.replaceChildren();

    const add = (message: string, kind: 'error' | 'warning') => {
      const line = document.createElement('div');
      line.className = `notice ${kind}`;
      line.textContent = message;
      messagesSlot.append(line);
    };

    for (const message of errors) add(message, 'error');
    for (const message of warnings) add(message, 'warning');
  };

  const renderDocument = () => {
    const { doc, validationErrors } = store.get();

    if (!doc) {
      documentSlot.hidden = false;
      documentSlot.innerHTML = `
        <div class="empty-state">
          <p><strong>Dépose ton fichier <code>${WORKING_FILENAME}</code> ici</strong></p>
          <p>ou utilise « Ouvrir… » — ou commence un « Nouveau » document.</p>
        </div>
      `;
      editorSlot.hidden = true;
      legendSlot.replaceChildren();
      bandsSlot.replaceChildren();
      return;
    }

    documentSlot.hidden = true;
    editorSlot.hidden = false;

    renderFilterBar(legendSlot, doc, { categories: hiddenCategories, bands: hiddenBands }, {
      onToggleCategory(id) {
        if (hiddenCategories.has(id)) hiddenCategories.delete(id);
        else hiddenCategories.add(id);
        renderDocument();
      },
      onSetAllCategories(visible) {
        hiddenCategories.clear();
        if (!visible) for (const id of Object.keys(doc.categories)) hiddenCategories.add(id);
        renderDocument();
      },
      onToggleBand(index) {
        if (hiddenBands.has(index)) hiddenBands.delete(index);
        else hiddenBands.add(index);
        renderDocument();
      },
      onSetAllBands(visible) {
        hiddenBands.clear();
        if (!visible) doc.bands.forEach((_, index) => hiddenBands.add(index));
        renderDocument();
      },
    });

    // A band too dense to print is a blocking error: the chart is not rendered
    // at all, rather than rendered illegibly (features.md §1).
    const bandErrors = validateBands(doc.bands);
    if (bandErrors.length > 0) {
      showMessages(bandErrors);
      bandsSlot.replaceChildren();
      editor.render(doc, () => null);
      return;
    }

    // Everything else (an unknown category, an inverted range) is reported but
    // still drawn, so it can be found and fixed.
    const isVisible = (item: Item) => !hiddenCategories.has(item.cat);
    const metrics = printGeometry ? PRINT_METRICS : SCREEN_METRICS;
    applyMetrics(root, metrics);

    // Hidden bands are dropped before layout rather than after, so a bar cut at
    // the edge of a hidden band does not claim to continue from something the
    // reader cannot see.
    const shown = { ...doc, bands: doc.bands.filter((_, index) => !hiddenBands.has(index)) };

    const layout = printGeometry
      ? layoutDocument(shown, printLayoutOptions(printSettings, { isVisible }))
      : layoutDocument(shown, { metrics, isVisible });

    // Warnings are always measured against the page, never against the screen:
    // changing the orientation has to surface "too dense for portrait" straight
    // away, not at the moment the print dialog opens.
    const onPaper = printGeometry
      ? layout
      : layoutDocument(shown, printLayoutOptions(printSettings, { isVisible }));

    // Non-blocking: a band taller than the page still works on screen, and only
    // the user can decide how to thin it out.
    showMessages(validationErrors, printWarnings(onPaper, printSettings));

    const lanes = new Map<number, number>();
    for (const band of layout.bands) {
      for (const bar of band.bars) lanes.set(bar.index, bar.lane);
      for (const event of band.events) lanes.set(event.index, event.lane);
    }

    renderTimeline(bandsSlot, doc, layout, metrics, editor.selectedItemIndex());
    editor.render(doc, (index) => lanes.get(index) ?? null);
  };

  // Clicking a bar or an event loads it into the entry form for editing.
  bandsSlot.addEventListener(
    'click',
    (event) => {
      const target = (event.target as HTMLElement).closest<HTMLElement>('[data-item-index]');
      const raw = target?.dataset['itemIndex'];
      editor.selectItem(raw === undefined ? null : Number(raw));
      renderDocument();
    },
    { signal },
  );

  const unsubscribe = store.subscribe((state) => {
    const hasDoc = state.doc !== null;
    saveButton.disabled = !hasDoc;

    printButton.disabled = !hasDoc;
    exportButton.disabled = !hasDoc;
    addEntryButton.disabled = !hasDoc;
    addEntryFab.disabled = !hasDoc;
    undoButton.disabled = !state.canUndo;
    redoButton.disabled = !state.canRedo;

    renderDocument();
  });

  /* ------------------------------------------------------------------ opening */

  /**
   * Asks before anything is thrown away. "Nouveau" always asks when a document
   * is open, even a saved one: it replaces the chart with nothing, and finding
   * the file again is a chore. Opening another file only asks when there are
   * unsaved changes — a document is being replaced by a document.
   */
  const confirmDiscard = async (always = false): Promise<boolean> => {
    if (store.isDirty()) {
      return confirmDialog.ask(
        'Le document courant a des modifications non enregistrées, les abandonner ?',
      );
    }
    if (always && store.get().doc) {
      return confirmDialog.ask('Fermer le document courant et repartir d’un document vide ?');
    }
    return true;
  };

  const applyOpenResult = (result: OpenResult) => {
    if (result.kind === 'cancelled') return;
    if (result.kind === 'invalid') {
      showMessages(result.errors);
      return;
    }
    showMessages([]);
    store.opened(result.doc);
  };

  action('open').addEventListener('click', () => {
    void confirmDiscard().then((ok) => {
      if (ok) return files.open().then(applyOpenResult);
      return undefined;
    });
  });

  action('new').addEventListener('click', () => {
    void confirmDiscard(true).then((ok) => {
      if (!ok) return;
      hiddenCategories.clear();
      hiddenBands.clear();
      showMessages([]);
      store.created(createEmptyDocument());
    });
  });

  enableFileDrop(
    documentSlot,
    (file) => {
      void confirmDiscard().then((ok) => {
        if (ok) return readDocumentFile(file).then(applyOpenResult);
        return undefined;
      });
    },
    signal,
  );

  /* ------------------------------------------------------------------- saving */

  const save = async (filename: string) => {
    const { doc } = store.get();
    if (!doc) return;

    const stamped = withSavedAt(doc);
    await files.save(stamped, filename);
    store.saved(stamped);
  };

  // A download gives almost no visual feedback once Firefox's save dialog is
  // disabled, so the button confirms the save on itself.
  const pulse = () => {
    saveButton.classList.remove('pulse');
    void saveButton.offsetWidth; // restart the animation
    saveButton.classList.add('pulse');
  };

  saveButton.addEventListener('click', () => {
    pulse();
    void save(WORKING_FILENAME);
  });

  for (const button of [addEntryButton, addEntryFab]) {
    button.addEventListener('click', () => editor.openNewEntry());
  }

  undoButton.addEventListener('click', () => store.undo());
  redoButton.addEventListener('click', () => store.redo());

  /* ------------------------------------------------------------ print / export */

  printButton.addEventListener('click', () => window.print());

  const orientationSelect = root.querySelector<HTMLSelectElement>('[data-action="print-orientation"]');

  orientationSelect?.addEventListener('change', () => {
    printSettings.orientation = orientationSelect.value === 'portrait' ? 'portrait' : 'landscape';
    applyPageSetup(printSettings);
    renderDocument();
  });

  exportButton.addEventListener('click', () => {
    const { doc } = store.get();
    if (!doc) return;

    const html = buildStandaloneHtml(doc, 'Chrono-Arrow', printSettings);
    triggerDownload(new Blob([html], { type: 'text/html' }), copyFilename(new Date(), 'html'));
  });

  // Firefox lays the page out for the printer between these two events, which is
  // the only moment the chart must use page geometry instead of screen geometry.
  window.addEventListener(
    'beforeprint',
    () => {
      printGeometry = true;
      renderDocument();
    },
    { signal },
  );

  window.addEventListener(
    'afterprint',
    () => {
      printGeometry = false;
      renderDocument();
    },
    { signal },
  );

  window.addEventListener(
    'keydown',
    (event) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      const key = event.key.toLowerCase();

      // Ctrl+S is deliberately not bound: saving is a button, and every save
      // writes a new file, so a key that fires it by reflex litters the folder.
      // Ctrl+Z / Ctrl+Shift+Z, plus Ctrl+Y for the Windows habit.
      if (key === 'z') {
        event.preventDefault();
        if (event.shiftKey) store.redo();
        else store.undo();
        return;
      }
      if (key === 'y') {
        event.preventDefault();
        store.redo();
      }
    },
    { signal },
  );

  /* ------------------------------------------------------- last line of defence */

  window.addEventListener(
    'beforeunload',
    (event) => {
      if (!store.isDirty()) return;
      event.preventDefault();
      event.returnValue = '';
    },
    { signal },
  );

  renderDocument();

  return () => {
    controller.abort();
    unsubscribe();
    root.innerHTML = '';
  };
}
